import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { useNotification } from "@lib/hooks/useNotification";
import { useTypedSelector } from "@lib/redux";
import { useQuery } from "@tanstack/react-query";
import { apiGetNotifications } from "@lib/api/notification";

const ProviderList = () => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { role } = useTypedSelector((state) => state.auth?.user);
  const { handleClick } = useNotification({ role, messages });
  const { unseenCount } = useTypedSelector((state) => state.notifications);

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
  });

  const notificationsQuery = useQuery({
    queryFn: () => apiGetNotifications(params),
    queryKey: ["notification", JSON.stringify(params)],
    enabled: Boolean(params),
  });

  const { data, isLoading, isFetching, refetch } = notificationsQuery;
  const apiData = data?.data?.data;

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        apiData,
        navigate,
        messages,
        handleClick,
      }),
    [apiData, params],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter.map((f) => ({
      key: f.split("||")[0],
      title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
      operator: f.split("||")[1],
      value: f.split("||")[2],
    })) as TableHeaderFilter[];
  }, [params]);

  useEffect(() => {
    if (!Boolean(params)) return;
    refetch();
  }, [unseenCount, params]);

  return (
    <AppsContainer
      fullView={true}
      title={messages["common.notifications"] as string}
      cardProps={{
        borderFull: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={notificationsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching}
        />
      </AppsContent>
    </AppsContainer>
  );
};

export default ProviderList;
